/**
 * preloadAssets.ts â€” FIX#3 idle-time prefetch.
 *
 * Warms the service-worker CacheFirst runtime caches (sprites-cache,
 * images-cache, audio-cache, cutscenes-cache) during menu / char-select idle
 * time so in-game first-access is instant instead of fetching from network.
 *
 * Rules:
 * - Low concurrency (CONCURRENCY = 5): non-blocking; won't saturate the network.
 * - `cache: "force-cache"`: if SW already cached it, hits cache; otherwise
 *   fetches and lets the SW CacheFirst handler store it.  Error swallowed.
 * - Idempotent: module-scope Promise guard means `preloadGameAssets()` is a
 *   no-op after the first call (even if called from multiple components).
 * - Does NOT block rendering or gameplay â€” fire-and-forget async.
 *
 * URL lists are derived from the same constants the game uses:
 *   - Player sprites: ARCHETYPES Ã— frames (CharSelect.tsx / SidescrollerScene.tsx)
 *   - NPC sprites: NPC_FILES Ã— {idle,greet}-0 (SidescrollerScene.tsx)
 *   - Terrain backgrounds: TERRAIN_KEYS (SidescrollerScene.tsx)
 *   - BGM: char-select + era-1 tracks (audio.ts BGM_URLS â€” first session tracks)
 *   - Intro cutscene frames: intro-1..6 (cutscene-data.ts / cutscenes.json)
 */

// ---------------------------------------------------------------------------
// Player archetypes (mirrors CharSelect.tsx ARCHETYPE_ORDER)
// ---------------------------------------------------------------------------

const ARCHETYPES = ["engineer", "scientist", "activist", "politician"] as const;
const PLAYER_FRAME_SUFFIXES = [
  "-portrait.png",
  "-idle-0.png",
  "-idle-1.png",
  "-walk-0.png",
  "-walk-1.png",
] as const;

// ---------------------------------------------------------------------------
// NPC sprite files (mirrors SidescrollerScene.tsx getNpcsForTileType + NPC_SCALE)
// NPC file names without extension, used as the base for `-idle-0.png`/`-greet-0.png`.
// SidescrollerScene only uses frame index 0 (idle/greet) at render time.
// ---------------------------------------------------------------------------

const NPC_BASE_NAMES = [
  "builder-e1",
  "scout-e1",
  "newsgirl-e1",
  "bureaucrat-e1",
  "guard",
  "inmate",
  "trader-e1",
  "keeper",
  "mayor",
] as const;

// ---------------------------------------------------------------------------
// Terrain background images (mirrors SidescrollerScene.tsx resolveTerrainKey)
// ---------------------------------------------------------------------------

const TERRAIN_KEYS = [
  "plains",
  "coastline",
  "desert",
  "forest",
  "mountain",
  "river",
  "tundra",
  "urban",
  "volcanic",
  "jail",
  "trading",
  "teleport",
  "event",
  "policy",
  "start",
] as const;

// ---------------------------------------------------------------------------
// BGM tracks to prefetch (audio.ts BGM_URLS â€” just the session-start tracks)
// Rationale: char-select plays on char-select screen; era-1-a/b play in game.
// Skip all cutscene / ending tracks (one-shot, not worth pre-warming).
// ---------------------------------------------------------------------------

const BGM_URLS_TO_PREFETCH = [
  "/audio/bgm/char-select-1.mp3",
  "/audio/bgm/char-select-2.mp3",
  "/audio/bgm/era-1-a.mp3",
  "/audio/bgm/era-1-b.mp3",
  "/audio/bgm/terrain-plains-1.mp3",
  "/audio/bgm/terrain-plains-2.mp3",
  "/audio/bgm/terrain-forest-1.mp3",
  "/audio/bgm/terrain-mountain.mp3",
  "/audio/bgm/terrain-desert.mp3",
  "/audio/bgm/terrain-coastline.mp3",
] as const;

// ---------------------------------------------------------------------------
// Intro cutscene frame images (cutscenes.json intro group: intro-1..6)
// ---------------------------------------------------------------------------

const INTRO_FRAME_COUNT = 6;

// ---------------------------------------------------------------------------
// URL list builders
// ---------------------------------------------------------------------------

function buildUrlList(): string[] {
  const urls: string[] = [];

  // Player sprites â€” 4 archetypes Ã— 5 frames
  for (const archetype of ARCHETYPES) {
    for (const suffix of PLAYER_FRAME_SUFFIXES) {
      urls.push(`/sprites/players/${archetype}${suffix}`);
    }
  }

  // NPC sprites â€” idle-0 and greet-0 for each NPC
  for (const base of NPC_BASE_NAMES) {
    urls.push(`/sprites/npcs-frames/${base}-idle-0.png`);
    urls.push(`/sprites/npcs-frames/${base}-greet-0.png`);
  }

  // Terrain backgrounds
  for (const key of TERRAIN_KEYS) {
    urls.push(`/images/terrain/backgrounds/bg_${key}.jpg`);
  }

  // BGM
  for (const url of BGM_URLS_TO_PREFETCH) {
    urls.push(url);
  }

  // Intro cutscene frames
  for (let i = 1; i <= INTRO_FRAME_COUNT; i++) {
    urls.push(`/cutscenes/intro-${i}.jpg`);
  }

  return urls;
}

// ---------------------------------------------------------------------------
// Canonical 12 facility IDs served at /images/facilities/facility_{id}.png
// (mirrors SidescrollerScene.tsx FACILITY_SCREEN_HEIGHT_PCT keyset,
//  excluding naturalgas / offshorewind which share other sprites)
// ---------------------------------------------------------------------------

const FACILITY_IDS = [
  "biomass",
  "coal",
  "floatingsolar",
  "geothermal",
  "hydro",
  "nuclear",
  "offshoredrilling",
  "oil",
  "solar",
  "tidal",
  "wastetoenergy",
  "wind",
] as const;

/**
 * Critical subset gated at game-start (Tier 1).
 *
 * Must-have before the board / SidescrollerScene becomes visible AND before
 * the opening intro cutscene plays:
 *   - cutscene-intro BGM     1 URL   ( ~0.4 MB) â€” avoids Howler stutter on intro
 *   - Intro cutscene frames  6 URLs  (~36.7 MB) â€” the opening cinematic
 *   - Player sprites        20 URLs  (~14.9 MB)
 *   - Terrain backgrounds   15 URLs  ( ~5.9 MB)
 *   - IslandMap (HexBoard)   1 URL   ( ~3.2 MB)
 *   - Facility sprites      12 URLs  (~16.4 MB)
 *   - era-1-a BGM            1 URL   ( ~1.6 MB) â€” first board BGM after intro
 *
 * Total: 56 URLs, ~79 MB worst-case (cold cache).
 *
 * GATE-PAYLOAD BYTE BUDGET (2026-06-13 — gate-first intro fix):
 *   The intro frames add ~36.7 MB to the gate. Measured on disk (assets/cutscenes):
 *     intro-1 5.21 · intro-2 5.74 · intro-3 6.49 · intro-4 6.22 · intro-5 5.80 ·
 *     intro-6 7.19  (MB) = 36.66 MB total.
 *   We deliberately gate ALL SIX (not just frame 0/1) per the user requirement
 *   "gate till done loading": with gate-first sequencing the intro cutscene
 *   begins the instant the gate dismisses (startIntroAfterGate fires on dismiss),
 *   so a slow click-through must never out-run the fetch of a later frame.
 *   The AssetLoadingGate STALL-detection "Start anyway" button (8 s with no new
 *   asset completing) is the escape hatch on a genuinely dead connection, so a
 *   slow iPad is never hard-stuck behind the 79 MB payload.
 *
 *   The intro-frame URLs below are derived to match cutscene-data's actual
 *   imgSrc values (`/cutscenes/intro-{1..6}.jpg`, verified against
 *   data/cutscenes.json). If those imgSrc values ever change, this list and the
 *   Lock R13 critical-list test must be updated together.
 *
 * Tier 2 (background warm, after gate dismisses â€” handled by preloadGameAssets()):
 *   - NPC sprites (~61 MB) â€” sidescroller-only, deferred to warm path
 *   - bg-loop-web-v2.mp4 (16 MB) â€” browser video streaming handles buffering
 *   - Remaining terrain BGM tracks â€” non-critical; era BGM covers landing
 *
 * WHY the old code EXCLUDED intro frames (now fixed):
 *   The prior rationale assumed the gate sat at z:2300 ABOVE the CutscenePlayer
 *   (z:2200) so the cutscene played UNDER the gate and the player never saw a
 *   blank first frame. That predates gate-first sequencing. The cutscene now
 *   does not mount until startIntroAfterGate() fires AT gate-dismiss, so frame 0
 *   is the very first thing shown — and CutscenePlayer only preloads idx+1/idx+2
 *   (never frame 0). On a slow iPad frame 0 fetched live → blank (the <img>
 *   onError fallback only fires on a 404, not on a slow load). Gating the frames
 *   guarantees they are cache-warm before the cutscene starts.
 *
 * BGM strategy: cutscene-intro.mp3 + era-1-a.mp3 are included here so their
 *   bytes are in the SW cache before Howler creates its Howl instance.
 *   html5:true Howler streams but still needs the initial fetch to succeed;
 *   force-cache here guarantees the SW CacheFirst handler has the response
 *   ready. With gate-first sequencing (2026-06-13) the intro BGM Howler stream
 *   only fires AFTER the gate dismisses â€” by then this fetch has completed.
 *   Keeps the same ordering for belt-and-suspenders safety on slow devices.
 */
export function buildCriticalUrlList(): string[] {
  const urls: string[] = [];

  // Intro cutscene BGM â€” 1 FIRST (critical ordering).
  // Gate-first sequencing (2026-06-13): CutscenePlayer no longer fires the
  // same tick the gate mounts. AssetLoadingGate calls startIntroAfterGate()
  // AFTER the gate dismisses (after this fetch completes). Placing this URL
  // first still ensures the SW CacheFirst cache is warm before Howler makes
  // its streaming request â€” belt-and-suspenders on slow devices.
  urls.push("/audio/bgm/cutscene-intro.mp3");

  // Intro cutscene frames â€” 6 (the opening cinematic, shown the instant the
  // gate dismisses). frame 0 (/cutscenes/intro-1.jpg) is NON-NEGOTIABLE here:
  // it is the first image the cutscene renders. URLs mirror cutscene-data's
  // imgSrc (`/cutscenes/intro-{i}.jpg`). See INTRO_FRAME_COUNT above.
  for (let i = 1; i <= INTRO_FRAME_COUNT; i++) {
    urls.push(`/cutscenes/intro-${i}.jpg`);
  }

  // Player sprites â€” 4 archetypes Ã— 5 frames = 20
  for (const archetype of ARCHETYPES) {
    for (const suffix of PLAYER_FRAME_SUFFIXES) {
      urls.push(`/sprites/players/${archetype}${suffix}`);
    }
  }

  // Terrain backgrounds â€” 15
  for (const key of TERRAIN_KEYS) {
    urls.push(`/images/terrain/backgrounds/bg_${key}.jpg`);
  }

  // HexBoard island map â€” 1
  urls.push("/images/board/IslandMap.png");

  // Facility sprites â€” 12
  for (const id of FACILITY_IDS) {
    urls.push(`/images/facilities/facility_${id}.png`);
  }

  // First board BGM â€” 1 (era 1 round 1-2 variant)
  urls.push("/audio/bgm/era-1-a.mp3");

  return urls; // 56 URLs total
}

// ---------------------------------------------------------------------------
// Batch fetch helpers
// ---------------------------------------------------------------------------

const CONCURRENCY = 5;

async function fetchInBatches(urls: string[]): Promise<void> {
  for (let i = 0; i < urls.length; i += CONCURRENCY) {
    const batch = urls.slice(i, i + CONCURRENCY);
    await Promise.allSettled(
      batch.map((url) =>
        fetch(url, { cache: "force-cache", priority: "low" } as RequestInit).catch(() => {
          /* swallow â€” prefetch is best-effort */
        }),
      ),
    );
  }
}

/**
 * Fetch the critical URL list one-by-one, calling `onProgress` after each
 * settled fetch. Uses `force-cache` (hits SW CacheFirst if warm). Errors are
 * swallowed â€” this must never reject or stall indefinitely.
 */
async function fetchCriticalWithProgress(
  urls: string[],
  onProgress: (loaded: number, total: number) => void,
): Promise<void> {
  const total = urls.length;
  let loaded = 0;
  for (const url of urls) {
    await fetch(url, { cache: "force-cache", priority: "high" } as RequestInit).catch(() => {
      /* swallow */
    });
    loaded += 1;
    onProgress(loaded, total);
  }
}

// ---------------------------------------------------------------------------
// Public API â€” idempotent fire-and-forget (background warm)
// ---------------------------------------------------------------------------

let _prefetchPromise: Promise<void> | null = null;

/**
 * Prefetch session assets in the background. Safe to call multiple times â€”
 * only the first call does real work. Does NOT block the caller.
 */
export function preloadGameAssets(): void {
  if (_prefetchPromise !== null) return;
  if (typeof fetch === "undefined") return; // SSR / test environment guard

  const urls = buildUrlList();
  _prefetchPromise = fetchInBatches(urls).catch(() => {
    /* top-level swallow â€” never throws */
  });
}

// ---------------------------------------------------------------------------
// Public API â€” gated critical prefetch with progress reporting
// ---------------------------------------------------------------------------

/**
 * Fetch the critical asset subset (player sprites, terrain BGs, IslandMap,
 * facility sprites, intro/era BGM) and report progress via `onProgress`.
 * Returns a Promise that resolves when all critical fetches settle.
 * Never rejects.
 *
 * Idempotent guard is intentionally NOT applied here â€” the gate component
 * calls this once per game-start and must always get fresh progress ticks.
 * The underlying `fetch(force-cache)` hits the SW CacheFirst cache if warm so
 * repeated calls for returning players are near-instant.
 *
 * @param onProgress Called after each URL settles: (loaded, total) â†’ pct.
 */
export function preloadGameAssetsCritical(
  onProgress?: (loaded: number, total: number) => void,
): Promise<void> {
  if (typeof fetch === "undefined") {
    // SSR / test environment â€” resolve immediately.
    onProgress?.(0, 0);
    return Promise.resolve();
  }

  const urls = buildCriticalUrlList();
  const cb = onProgress ?? (() => undefined);
  return fetchCriticalWithProgress(urls, cb).catch(() => {
    /* top-level swallow */
  }) as Promise<void>;
}

/** Test-only: reset the idempotent guard. */
export function _resetPrefetchForTesting(): void {
  _prefetchPromise = null;
}
